/**
 * IT Admin → Teams bot (19 Oct): the bot's status, the P1 channel and a test
 * card. Admins only; the server refuses everyone else.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getTeamsBotStatus,
  sendTeamsBotTestCard,
  setTeamsBotBackofficeChannel,
  setTeamsBotP1Channel,
  type TeamsBotStatus,
  type TeamsBotTestCardResponse,
} from "@/lib/api";
import { logger } from "@/lib/logger";

export const teamsBotKeys = {
  status: ["teams-bot", "status"] as const,
};

/** Switched on, Bot ID and secret set (never the secret), installs, the P1 channel. */
export function useTeamsBotStatus() {
  return useQuery({
    queryKey: teamsBotKeys.status,
    queryFn: async (): Promise<TeamsBotStatus> => {
      const { data } = await getTeamsBotStatus({ throwOnError: true });
      return data;
    },
  });
}

/** Choose the P1 channel (a conversation id from `channels`), or clear it (null). */
export function useSetP1Channel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string | null): Promise<TeamsBotStatus> => {
      logger.debug("[teams-bot] set P1 channel", conversationId ?? "(none)");
      const { data } = await setTeamsBotP1Channel({
        body: { conversation_id: conversationId },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (status) => {
      queryClient.setQueryData(teamsBotKeys.status, status);
    },
  });
}

/**
 * Choose the Backoffice channel (21 Oct): where "new user ready for
 * Backoffice" cards go when the bot is on; null clears it (the webhook then).
 */
export function useSetBackofficeChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string | null): Promise<TeamsBotStatus> => {
      logger.debug("[teams-bot] set Backoffice channel", conversationId ?? "(none)");
      const { data } = await setTeamsBotBackofficeChannel({
        body: { conversation_id: conversationId },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (status) => {
      queryClient.setQueryData(teamsBotKeys.status, status);
    },
  });
}

/** The welcome card to the signed-in admin's own Teams chat with the bot. */
export function useSendTestCard() {
  return useMutation({
    mutationFn: async (): Promise<TeamsBotTestCardResponse> => {
      logger.debug("[teams-bot] test card");
      const { data } = await sendTeamsBotTestCard({ throwOnError: true });
      return data;
    },
  });
}
